import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { ToastProvider } from '@xcentral/ui';
import { getMsalInstance } from '../auth/msalInstance';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ScrollToTop } from '../components/ScrollToTop';
import { BrandProvider, StaticBrandProvider } from '../theme/BrandProvider';

/**
 * True in the browser when the page carries no server-rendered markup (the SPA
 * shell, Bug #20802) — false during SSG (no document) and on prerendered pages,
 * which must keep the hydration-parity first render (Bug #16825).
 */
function isClientRenderedPage(): boolean {
  return typeof document !== 'undefined' && document.querySelector('[data-server-rendered=true]') === null;
}

export function RootLayout() {
  // Bug #16825 — the FIRST client render must reproduce what the server rendered,
  // or hydration fails for the whole root.
  //
  // The trap: main.tsx awaits initializeMsalInstance() BEFORE hydrating, because
  // handleRedirectPromise() must resolve before React renders (CLAUDE.md, MSAL
  // pattern — that ordering is not negotiable and is NOT what changed here). So by
  // the time hydration ran, getMsalInstance() was already non-null and this layout
  // took the provider branch — while the prerendered HTML had taken the null branch.
  // Two different trees, so React reported "Expected server HTML to contain a
  // matching <div> in <div>" (#418, once per differing node) and then #423, which
  // discards the entire server-rendered root and re-renders it client-side.
  //
  // The fix is to defer the UPGRADE, not the MSAL initialisation: render #1 takes
  // the same branch the server took, hydration matches, and the provider tree
  // arrives on render #2 as an ordinary update. MSAL is still fully initialised
  // before any of this — only the moment its provider mounts has moved.
  //
  // Consequence, stated rather than hidden: switching StaticBrandProvider ->
  // BrandProvider changes the component type, so React remounts the subtree below
  // it once. That was already happening — via the far more expensive #423 path —
  // so this is strictly better, but it is not free.
  //
  // Bug #20802 — the deferral applies ONLY when hydrating server-rendered markup.
  // Signed-in routes are served the empty SPA shell (no data-server-rendered) and
  // are render()ed, not hydrated, so there is no server tree to match. Deferring
  // there left render #1 without MsalProvider, and AuthGuard's effect then called
  // loginRedirect on msal-react's default stub instance
  // ("stubbed_public_client_application_called", logged on every first load of a
  // signed-in route). On a shell page the provider tree mounts on render #1.
  const [hydrated, setHydrated] = useState(isClientRenderedPage);
  useEffect(() => {
    setHydrated(true);
  }, []);

  const instance = hydrated ? getMsalInstance() : null;

  const content = (
    <>
      <ScrollToTop />
      <Outlet />
    </>
  );

  if (!instance) {
    // SSG render (Node), and the first client render for hydration parity.
    // No MSAL/Toast context. Auth-aware UI inside descendants must guard with
    // <ClientOnly>. Brand defaults to Conscierra (StaticBrandProvider never fetches).
    return (
      <ErrorBoundary>
        <StaticBrandProvider>{content}</StaticBrandProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <MsalProvider instance={instance}>
        <ToastProvider>
          <BrandProvider>{content}</BrandProvider>
        </ToastProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}
